/**
 * @fileoverview Error handling utilities.
 * @summary Utility functions for translating and handling API errors.
 *
 * @package KaraoNow
 * @module apps/front/src/utils/errorUtils
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */

import type { TFunction } from 'i18next'

/**
 * @summary Get translated error message from error object.
 * @description Translates error codes from backend API responses using i18n.
 * Handles both simple error codes and Zod validation errors.
 *
 * @param {any} error - Error object from API (may have code, message, errors properties).
 * @param {TFunction} t - Translation function from i18next.
 * @returns {string} Translated error message or fallback message.
 *
 * @example
 * ```typescript
 * try {
 *   await dispatch(createEvent(eventData)).unwrap()
 * } catch (err: any) {
 *   const error = err.payload || err
 *   const errorMessage = getErrorMessage(error, t)
 *   setError(errorMessage)
 * }
 * ```
 */
export function getErrorMessage(error: any, t: TFunction): string {
  // If error has a code, try to translate it
  if (error.code) {
    const translated = t(`errors.${error.code}`)
    if (translated && translated !== `errors.${error.code}`) {
      return translated
    }
  }

  // For VALIDATION_ERROR, extract field and code from Zod errors
  if (error.code === 'VALIDATION_ERROR' && error.errors && Array.isArray(error.errors) && error.errors.length > 0) {
    const firstError = error.errors[0]
    const field = firstError.path && firstError.path.length > 0 ? firstError.path[firstError.path.length - 1] : null
    const zodCode = firstError.code

    if (field && zodCode) {
      const key = `errors.validation.${field}.${zodCode}`
      const translated = t(key)
      if (translated && translated !== key) {
        return translated
      }
    }
  }

  // Fallback to error message
  return error.message || t('errors.genericMessage') || 'An error occurred'
}
